import moment from "moment";
import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  useFetchVisitTypesMutation,
  useFetchDispatchProvidersMutation,
  useFetchProviderDispatchDataMutation,
  useCreateNewPatientAppointmentMutation,
} from "../../features/appointment/appointmentApiSlice";
import PatientSchedulingModal from "./PatientSchedulingModal";
import Loading from "../../components/Loading";
import DateFilter from "./components/DateFilter";
import { Icon } from "@virtuslab/tetrisly-icons";
import { Alert, Snackbar, Tooltip } from "@mui/material";
import { useFetchPlannerProvidersMutation } from "../../features/provider/providerApiSlice";
import { useFetchPlannerPatientsMutation } from "../../features/patient/patientApiSlice";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { v4 as uuidv4 } from "uuid";
import TableComponent from "./components/TableComponent";
import ScheduleConfirmation from "./components/ScheduleConfirmation";
import Map from "./components/Map";
import AddAppointment from "./components/AddAppointment";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import {
  genderPreferenceOptions,
  routingOptions,
  dispatchSortOptions,
  ACTIVITY_TYPE,
  ACTIVITY_STATUS,
  APPOINTMENT_CREATED_FROM,
} from "../../constants/config.constants";
import InfoModal from "../../components/Modals/InfoModal";
import { useEffectSkipFirst } from "../../hooks/useEffectSkipFirst";
import { useDispatchRenderTiming } from "../../hooks/useDispatchRenderTiming";
import { useCreateLogsMutation } from "../../features/logs/logsApiSlice";
import FilterComponentV2 from "./components/FilterComponentV2";

const PROSPECT_TYPE_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Member", value: "MEMBER" },
  { label: "Patient", value: "PATIENT" },
];

const TIME_OF_DAY_OPTIONS = [
  { label: "Any time", value: "Any" },
  { label: "Morning (6am-12pm)", value: "MORNING" },
  { label: "Afternoon (12pm-5pm)", value: "AFTERNOON" },
  { label: "Evening (5pm-9pm)", value: "EVENING" },
];

const PROVIDER_TYPE_OPTIONS = [
  { label: "Any", value: "Any" },
  { label: "Per Diem", value: "Per Diem" },
  { label: "Full-Time", value: "Full-Time" },
];

const PatientSchedulingWizard = () => {
  const location = useLocation();
  const user = useSelector(selectUser);
  const containerRef = useRef(null);
  const skipNextDateRefetchRef = useRef(false);
  const dispatchTimingForResultsRef = useRef(null);
  const {
    startTiming: startDispatchTiming,
    markResponseReceived: markDispatchResponseReceived,
    markProcessReady: markDispatchProcessReady,
    markEtaStarted: markDispatchEtaStarted,
    markEtaCompleted: markDispatchEtaCompleted,
    markFailure: markDispatchFailure,
    markRenderReady: markDispatchRenderReady,
  } = useDispatchRenderTiming();
  const [getDispatchProviders, { isLoading: isDispatchLoading }] =
    useFetchDispatchProvidersMutation();
  const [getProviderDispatchData, { isLoading: isProviderDispatchLoading }] =
    useFetchProviderDispatchDataMutation();

  const [providerAppointments, setProviderAppointments] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [providersData, setProvidersData] = React.useState([]);
  const [possibleInsertionsPerGroup, setPossibleInsertionsPerGroup] =
    React.useState(null);
  const [dispatchErrors, setDispatchErrors] = React.useState(null);

  const [filterOpen, setFilterOpen] = React.useState(true);
  const [dispatchId, setDispatchId] = React.useState(null);

  const [dates, setDates] = React.useState([]);

  const [selectedDate, setSelectedDate] = React.useState(null);
  const [view, setView] = React.useState("list");

  const [startDate, setStartDate] = React.useState(null);

  const [endDate, setEndDate] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [openProviderAppointments, setOpenProviderAppointments] =
    React.useState(false);
  const [appointmentType, setAppointmentType] = React.useState(null);
  const [createAppointment, setCreateAppointment] = React.useState(null);
  const [patients, setPatients] = React.useState([]);
  const [appointmentVisitType, setAppointmentVisitType] = React.useState(null);

  const [providers, setProviders] = React.useState([]);
  const [appointmentTypes, setAppointmentTypes] = React.useState([]);
  const [prospectType, setProspectType] = React.useState(
    PROSPECT_TYPE_OPTIONS[0]
  );
  const [providerType, setProviderType] = React.useState(
    PROVIDER_TYPE_OPTIONS[0]
  );
  const [timeOfDay, setTimeOfDay] = React.useState(TIME_OF_DAY_OPTIONS[0]);
  const [routingCriteria, setRoutingCriteria] = React.useState(
    routingOptions[0]
  );

  const [providerAppointment, setProviderAppointment] = React.useState(null);

  const [appointments, setAppointments] = React.useState([]);

  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const selectedPatientRef = useRef(selectedPatient);
  const visitTypeFetchSeqRef = useRef(0);
  useEffect(() => {
    selectedPatientRef.current = selectedPatient;
  }, [selectedPatient]);

  const [selectedProvider, setSelectedProvider] = React.useState(null);
  const [languagePreference, setLanguagePreference] = React.useState([
    {
      label: "English",
      value: "English",
    },
  ]);
  const [genderPreference, setGenderPreference] = React.useState(
    genderPreferenceOptions[0]
  );
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);

  const [updatedAppointments, setUpdatedAppointments] = React.useState([]);

  const [uniqueLocations, setUniqueLocations] = React.useState([]);
  const [openMap, setOpenMap] = React.useState(false);
  const [updates, setUpdates] = React.useState([]);
  const [visitType, setVisitType] = React.useState("NORMAL");
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [addAppointmentError, setAddAppointmentError] = React.useState("");
  const [isPublished, setIsPublished] = React.useState(false);
  const [filterError, setFilterError] = React.useState(null);
  const [selectedVisitLocation, setSelectedVisitLocation] =
    React.useState(null);

  const [searchConsecutiveSlots, setSearchConsecutiveSlots] = React.useState(false);

  const isLoading = isDispatchLoading || isProviderDispatchLoading;

  const [fetchPlannerPatients] = useFetchPlannerPatientsMutation();
  const [fetchPlannerProviders] = useFetchPlannerProvidersMutation();
  const [fetchVisitTypes] = useFetchVisitTypesMutation();
  const [createNewAppointment, { isLoading: createLoad }] =
    useCreateNewPatientAppointmentMutation();
  const [createLog] = useCreateLogsMutation();

  const patientPromiseOptions = (inputValue, patientId = null) =>
    new Promise((resolve) => {
      fetchPlannerPatients({
        search: inputValue,
        primaryProviderIds: [],
        patientIds: patientId ? [patientId] : [],
        zoneIds: [],
        take: 50,
        prospectType: prospectType?.value ?? "ALL",
        showInactive: true,
      }).then((res) => {
        const patients = res?.data?.data?.map((patient) => ({
          value: patient.id,
          label: `${patient.name} | DOB: ${patient.dob ? moment(patient.dob).format("MM/DD/YYYY") : "NA"
            } | EHR ID: ${patient.ehrId} | Type: ${patient.isMember ? "Member" : "Patient"
            }`,
          departmentId: patient.departmentId,
          location: patient.location,
          languagePreference: patient.languagePreference,
          languages: patient.languages,
          genderPreference: patient.genderPreference,
        }));
        resolve(patients);
        if (patientId && patients?.[0]) {
          setSelectedPatient(patients[0]);
          const preloaded = patients[0];
          const langFromPatient =
            preloaded.languagePreference != null
              ? [
                {
                  label: preloaded.languagePreference,
                  value: preloaded.languagePreference,
                },
              ]
              : preloaded.languages?.length
                ? preloaded.languages.map((lang) => ({
                  label: lang,
                  value: lang,
                }))
                : [{ label: "English", value: "English" }];
          setLanguagePreference(langFromPatient);
        }
        setPatients(patients);
      });
    });

  const providerPromiseOptions = (inputValue) =>
    new Promise((resolve) => {
      fetchPlannerProviders({
        search: inputValue,
        primaryProviderIds: [],
        patientIds: [],
        zoneIds: [],
        take: 50,
      }).then((res) => {
        const providers = res?.data?.data?.map((provider) => ({
          label: provider.name,
          // value: provider.id,
          value: { id: provider.id, provider_market_id: provider.market_id },
        }));
        resolve(providers);
        setProviders(providers);
      });
    });

  const visitTypePromiseOptions = (inputValue) =>
    new Promise((resolve) => {
      const search =
        typeof inputValue === "string"
          ? inputValue
          : inputValue?.value ?? "";
      const patientId =
        typeof inputValue === "string"
          ? undefined
          : inputValue?.patientId;
      if (patientId == null || patientId === "") {
        resolve(undefined);
        return;
      }
      const seq = ++visitTypeFetchSeqRef.current;
      fetchVisitTypes({
        search,
        take: 50,
        patientId,
      })
        .then((res) => {
          if (seq !== visitTypeFetchSeqRef.current) {
            resolve(undefined);
            return;
          }
          const appointmentTypes = res?.data?.data
            ?.map((appointmentType) => ({
              label: appointmentType.name,
              value: appointmentType.id,
              duration: appointmentType.duration,
              ehr_id: appointmentType.appointment_id_in_ehr,
              isTelehealth: appointmentType.is_telehealth,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          resolve(appointmentTypes);
          setAppointmentTypes(appointmentTypes);
        })
        .catch((err) => {
          console.log(err);
          resolve(undefined);
        });
    });
  const [openAlert, setOpenAlert] = React.useState(false);
  const [message, setMessage] = React.useState("Something went wrong");
  const [severity, setSeverity] = React.useState("error");
  const showAlert = (msg, severity) => {
    setOpenAlert(true);
    setMessage(msg);
    setSeverity(severity);
  };
  useEffect(() => {
    providerPromiseOptions("");
  }, []);

  useEffect(() => {
    if (selectedPatient?.value == null) {
      setAppointmentType(null);
      setAppointmentVisitType(null);
      setAppointmentTypes([]);
      return;
    }
    visitTypePromiseOptions({
      value: "",
      patientId: selectedPatient.value,
    });
  }, [selectedPatient?.value]);

  // Clear previously fetched results whenever the patient or appointment type
  // filter changes, so stale data isn't shown again once results are re-displayed.
  useEffectSkipFirst(() => {
    setData([]);
    setProviderAppointments([]);
    setProvidersData([]);
    setDates([]);
    setPage(1);
    setHasMore(true);
    setDispatchId(null);
    setPossibleInsertionsPerGroup(null);
    setDispatchErrors(null);
  }, [selectedPatient?.value, appointmentType?.value]);

  const getDateDay = (date) => {
    return moment(date, "YYYY-MM-DD").format("dddd").toUpperCase();
  };

  // const getFilteredData = (data, providerDetails) => {
  //   return [...data]
  //   // return data?.filter((item) => {
  //   //   // const provider = providerDetails?.find(
  //   //   //   (pro) => pro.id === item.providerId
  //   //   // );
  //   //   // const preference = provider?.dayPreferences?.[getDateDay(item?.date)] || provider?.dayPreferences?.ALL;
  //   //   return (
  //   //     !appointmentType?.value
  //   //     // provider?.dayPreferences?.ALL?.appointment_type_ids?.includes(
  //   //     //   Number(appointmentType?.value)
  //   //     // )
  //   //     // &&
  //   //     //   provider?.dayPreferences?.monthlyAvailability?.find((day) =>
  //   //     //     day.dates.includes(moment(selectedDate).format("YYYY-MM-DD"))
  //   //     //   )
  //   //   );
  //   // });
  // };

  const fetchProviders = async (reset = false) => {
    const requestId = uuidv4();
    dispatchTimingForResultsRef.current = null;
    const timingId = startDispatchTiming({
      requestId,
      view,
      mode: "provider",
    });

    try {
      const response = await getDispatchProviders({
        requestId,
        startDate,
        endDate,
        patientId: +selectedPatient.value,
        providerId: selectedProvider?.value
          ? +selectedProvider?.value?.id || selectedProvider?.value
          : undefined,
        routingCriteria: routingCriteria?.value
          ? routingCriteria?.value === null
            ? null
            : +routingCriteria?.value
          : undefined,
        selectedDate:
          view === "providers"
            ? selectedDate
              ? moment(selectedDate).format("YYYY-MM-DD")
              : startDate
            : null,
        languagePreference: (() => {
          if (
            languagePreference?.length === 0 ||
            languagePreference?.[0]?.value === "None"
          )
            return null;
          return languagePreference?.map((language) => language.value) || null;
        })(),
        gender:
          genderPreference?.value === "Any"
            ? null
            : genderPreference?.value || null,
        appointmentTypeId: appointmentType?.value || null,
        employmentType: providerType?.value ?? "Any",
        timeOfDay: timeOfDay?.value ?? "Any",
        visitLocationId:
          selectedVisitLocation === null
            ? null
            : selectedVisitLocation?.addressType === "PRIMARY"
              ? null
              : selectedVisitLocation?.id,
        isConsecutiveSlots: searchConsecutiveSlots,
        marketId: selectedProvider?.value
          ? +selectedProvider?.value?.provider_market_id
          : undefined,
      }).unwrap();
      const dispatchData = response?.data;

      if (dispatchData != null) {
        const possibleInsertions = Array.isArray(
          dispatchData.possibleInsertions
        )
          ? dispatchData.possibleInsertions
          : [];
        const shouldMeasureRender = markDispatchResponseReceived(timingId, {
          dispatchId: dispatchData.dispatchId,
          returnedResultCount: possibleInsertions.length,
          view,
        });

        // const filteredData = getFilteredData(
        //   dispatchData.possibleInsertions,
        //   dispatchData.providerDetails
        // );
        const filteredData = [...possibleInsertions];
        dispatchTimingForResultsRef.current = shouldMeasureRender
          ? {
              results: filteredData,
              timingId,
              requestId,
              dispatchId: dispatchData.dispatchId,
            }
          : null;
        setData(filteredData);
        setProvidersData(dispatchData.providerDetails);
        setPage(1);
        setDates(dispatchData.possibleInsertionsPerDay);
        setHasMore(true);
        setDispatchId(dispatchData.dispatchId);
        setPossibleInsertionsPerGroup({
          ...dispatchData.possibleInsertionsPerGroup,
        });
        setDispatchErrors(dispatchData.errors);
        if (selectedDate === null || reset) {
          skipNextDateRefetchRef.current = true;
          setSelectedDate(dispatchData.possibleInsertionsPerDay[0]?.date);
        }
      } else {
        markDispatchFailure(timingId, {
          failedStage: "dispatch_api",
          errorType: "empty_response",
        });
      }
    } catch (error) {
      const isTimeout =
        error?.status === "TIMEOUT_ERROR" || error?.error === "TIMEOUT_ERROR";
      markDispatchFailure(timingId, {
        status: isTimeout ? "timeout" : "error",
        failedStage: "dispatch_api",
        httpStatus: error?.status,
        errorType: error?.data?.message ?? error?.error ?? "request_failed",
      });
      console.log(error);
    }
  };

  // Debounce the fetchOptions function
  const patientOptions = useCallback(
    debounce((inputValue, callback) => {
      patientPromiseOptions(inputValue).then(callback);
    }, 500), // 500ms debounce
    [prospectType]
  );
  const providerOptions = useCallback(
    debounce((inputValue, callback) => {
      providerPromiseOptions(inputValue).then(callback);
    }, 500), // 500ms debounce
    [prospectType]
  );
  const visitTypeOptions = useCallback(
    debounce((inputValue, callback) => {
      if (selectedPatientRef.current?.value == null) {
        callback?.([]);
        return;
      }
      const payload =
        typeof inputValue === "string"
          ? {
            value: inputValue,
            patientId: selectedPatientRef.current?.value,
          }
          : inputValue;
      visitTypePromiseOptions(payload).then(callback);
    }, 400),
    [prospectType]
  );

  const getClosestDistance = async (
    appointments,
    { providerId, sequence, proposedPosition, proposedTimeslot, score, marketId }
  ) => {
    const updatedAppointments = sequence?.map((appointment) => {
      const appt = appointments?.appointments?.find(
        (appt) => appt?.patient?.id === appointment.patientId
      );
      const [, , timezone] = (appt?.timeslot || proposedTimeslot).split(" ");
      if (appointment.isDispatch) {
        const dispatchAppointment = {
          patient_id: appointment?.patientId,
          provider_id: providerId,
          appointmentType: {
            id: appointmentType?.value,
            name: appointmentType?.label,
            duration: appointmentType?.duration,
            isTelehealth: appointmentType?.isTelehealth,
          },
          proposedPatient: true,
          visitDate: moment(selectedDate).format("YYYY-MM-DD"),
          location: {
            ...appointments.patient?.location,
            coordinates: {
              latitude: appointments.patient?.location?.latitude,
              longitude: appointments.patient?.location?.longitude,
            },
          },
          patient: appointments.patient,
          provider: appointments.provider,
          dispatchId,
          timeslot: `${moment(appointment.timeslot, "HH:mm").format(
            "h:mm A"
          )} ${timezone}`,
          sequence: appointment.position,
          sequenceNumber: appointment.position,
          distance: appointment.distanceFromPreviousAppointment,
          distanceFromHome: appointment.distanceFromProvider,
          duration: appointment.durationFromPreviousAppointment,
          durationFromHome: appointment.durationFromProvider,
          matchScore: score,
          marketId: marketId ?? null,
        };
        setCreateAppointment(dispatchAppointment);
        return dispatchAppointment;
      }
      if(!appt || !appt?.patient?.id) return null;
      return {
        ...appt,
        timeslot: `${moment(appointment.timeslot, "HH:mm").format(
          "h:mm A"
        )} ${timezone}`,
        sequence: appointment.position,
        distance: appointment.distanceFromPreviousAppointment,
        distanceFromHome: appointment.distanceFromProvider,
        duration: appointment.durationFromPreviousAppointment,
        durationFromHome: appointment.durationFromProvider,
      };
    }).filter((appointment) => appointment != null);

    const updates = updatedAppointments
      .filter((appointment) => appointment.appointmentId != null)
      .map((appointment) => {
        return {
          id: appointment.appointmentId,
          sequenceNumber: appointment.sequence,
          distanceFromPreviousAppointment: +appointment.distance,
          durationFromPreviousAppointment: +appointment.duration,
          timeslot: moment(appointment.timeslot, "h:mm A").format("HH:mm"),
        };
      });

    setUpdates(updates);

    setUpdatedAppointments(updatedAppointments);

    setProviderAppointment((prev) => ({
      ...prev,
      position: proposedPosition,
    }));

    const locationMap = updatedAppointments.reduce(
      (acc, appointment, index) => {
        if (!acc[appointment.location.id]) {
          acc[appointment.location.id] = {
            location: appointment.location,
            appointments: [],
          };
        }
        acc[appointment.location.id].appointments.push({
          sequence: index + 1,
          patient: appointment.patient?.name,
          proposedPatient: appointment.proposedPatient,
        });
        return acc;
      },
      {}
    );

    const uniqueLocations = Object.values(locationMap);

    setUniqueLocations(uniqueLocations);
  };

  const fetchAppointmentsFunction = async (providerAppointment) => {
    try {
      console.log(providerAppointment, "LLO")
      const { data } = await getProviderDispatchData({
        providerId: providerAppointment.providerId,
        patientId: selectedPatient?.value,
        date:
          view === "providers"
            ? selectedDate
              ? moment(selectedDate).format("YYYY-MM-DD")
              : startDate
            : providerAppointment?.date,
        appointmentTypeId: appointmentType?.value,
        visitLocationId:
          selectedVisitLocation === null
            ? null
            : selectedVisitLocation?.addressType === "PRIMARY"
              ? null
              : selectedVisitLocation?.id,
        marketId: providerAppointment?.marketId ?? selectedProvider?.value?.provider_market_id,
      }).unwrap();
      setAppointments(data);
      await getClosestDistance(data, providerAppointment);
    } catch (err) {
      console.log(err, "err");
    }
  };
  const handleClose = () => {
    setOpen(false);
    setCreateAppointment(null);
  };
  React.useEffect(() => {
    const patientId = new URLSearchParams(location.search).get('patientId');
    patientPromiseOptions("", patientId);
  }, [prospectType, location.search]);

  useEffectSkipFirst(() => {
    if (skipNextDateRefetchRef.current) {
      skipNextDateRefetchRef.current = false;
      return;
    }
    submitEvent();
  }, [selectedDate, view]);

  const onSubmitFilters = (e, callback = true, reset = false) => {
    if (
      startDate &&
      endDate &&
      selectedPatient !== null &&
      appointmentType !== null
    ) {
      fetchProviders(reset);
    } else {
      callback &&
        setFilterError(
          "Please select a patient, start date, end date and visit type to fetch appointments"
        );
      setProviderAppointments([]);
    }
  };

  const submitEvent = (e) => {
    onSubmitFilters(e, false, false);
  };

  const [selectedOption, setSelectedOption] = React.useState(
    dispatchSortOptions[0]
  );

  const resetAllandFetch = async () => {
    setProviderAppointments([]);
    setProviderAppointment(null);
    setAppointments([]);
    setDates([]);
    // setStartDate("");
    // setEndDate("");
    setFilterOpen(true);
    setSelectedPatient(null);
    setSelectedProvider(null);
    setAppointmentType(null);
    setAppointmentVisitType(null);
    setRoutingCriteria(routingOptions[0]);
    setGenderPreference(genderPreferenceOptions[0]);
    setLanguagePreference([
      {
        label: "English",
        value: "English",
      },
    ]);
    setSelectedDate("");
    setOpenProviderAppointments(false);
    setUpdatedAppointments([]);
    setUniqueLocations([]);
    setOpenConfirmModal(false);
    setUpdates([]);
    setDispatchId(null);
    setIsPublished(false);
    setView("list");
    setPage(1);
    setHasMore(true);
    setFilterError(null);
    setPossibleInsertionsPerGroup(null);
    setDispatchErrors(null);
    setTimeOfDay(TIME_OF_DAY_OPTIONS[0]);
    setProviderType(PROVIDER_TYPE_OPTIONS[0]);
    setSearchConsecutiveSlots(false);


    const today = moment().format("YYYY-MM-DD");
    const tomorrow = moment().add(1, 'day').format("YYYY-MM-DD");
    const fixedEndDate = moment().add(1, 'day').add(28, 'days').format("YYYY-MM-DD");

    if (user?.permissions?.includes('dispatch.edit_dispatch_end_date')) {
      setStartDate(tomorrow);
      setEndDate(fixedEndDate);
    }
    else {
      setStartDate(today);
      setEndDate("");
    }
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container || isLoading || !hasMore) return;

    const isBottom =
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + 10;

    if (isBottom) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const dispatchTiming = dispatchTimingForResultsRef.current;
    const isInitialRenderForResponse = dispatchTiming?.results === data;

    if (data.length === 0) {
      // A genuinely empty result must clear the table - otherwise it keeps
      // showing whatever the previous search (or previous tab's fetch)
      // populated, which is how List View and Providers view could end up
      // displaying different result sets for the same search.
      setProviderAppointments([]);
      setHasMore(false);
      if (isInitialRenderForResponse) {
        markDispatchEtaCompleted(dispatchTiming.timingId, {
          status: "skipped",
          returnedCount: 0,
          failedCount: 0,
        });
        markDispatchRenderReady(dispatchTiming.timingId, 0);
      }
      return;
    }
    if (hasMore) {
      // const filteredData = getFilteredData(data, providersData);
      const filteredData = [...data];
      let newProviderAppointments;
      if (selectedOption.value === 1) {
        newProviderAppointments = filteredData.sort(
          (a, b) => b.score - a.score
        );
      } else if (selectedOption.value === 2) {
        newProviderAppointments = filteredData.sort((a, b) => {
          const distanceA = a.proposedDistance - b.originalDistance;
          const distanceB = b.proposedDistance - b.originalDistance;
          return distanceA - distanceB;
        });
      } else if (selectedOption.value === 3) {
        newProviderAppointments = filteredData.sort(
          (a, b) => a.proposedDistance - b.proposedDistance
        );
      } else if (selectedOption.value === 4) {
        newProviderAppointments = filteredData.sort(
          (a, b) => a.visitCount - b.visitCount
        );
      } else if (selectedOption.value === 5) {
        newProviderAppointments = filteredData.sort((a, b) => {
          const visitCountA = providersData?.find(
            (provider) => provider.id === a.providerId
          )?.patientCount;
          const visitCountB = providersData?.find(
            (provider) => provider.id === b.providerId
          )?.patientCount;
          return visitCountA - visitCountB;
        });
      } else {
        newProviderAppointments = filteredData.slice(0, page * 30);
      }
      setProviderAppointments(newProviderAppointments);
      setHasMore(filteredData.slice(page * 30).length > 0);
      if (isInitialRenderForResponse) {
        markDispatchProcessReady(dispatchTiming.timingId);
      }
    }
  }, [
    page,
    data,
    providersData,
    appointmentType?.value,
    selectedOption,
    hasMore,
    markDispatchEtaCompleted,
    markDispatchProcessReady,
    markDispatchRenderReady,
  ]);

  function getNextConsecutiveAppointment(appointmentsArray, selectedAppointment, durationL) {
    if (!selectedAppointment) return null;

    const selectedTime = selectedAppointment.timeslot || selectedAppointment.proposedTimeslot;
    const duration = durationL || selectedAppointment?.appointmentType?.duration;
    const selectedProviderId = selectedAppointment.provider_id || selectedAppointment.providerId;
    const selectedDate = selectedAppointment.visitDate || selectedAppointment.date;
    if (!selectedTime || !duration || !selectedProviderId || !selectedDate) return null;

    // Get all appointments for the same provider on the same date
    const sameDayAppointments = appointmentsArray.filter(appt =>
      (appt.providerId === selectedProviderId || appt.provider_id === selectedProviderId) &&
      (appt.date === selectedDate || appt.visitDate === selectedDate)
    );
    if (sameDayAppointments.length === 0) return null;

    // Sort appointments by time
    sameDayAppointments.sort((a, b) => {
      const timeA = a.timeslot || a.proposedTimeslot;
      const timeB = b.timeslot || b.proposedTimeslot;
      const dateA = new Date(`1970-01-01 ${timeA?.replace("EST", "").trim() || '00:00'}`);
      const dateB = new Date(`1970-01-01 ${timeB?.replace("EST", "").trim() || '00:00'}`);
      return dateA - dateB;
    });

    // Calculate next appointment time
    const selectedTimeDate = new Date(`1970-01-01 ${selectedTime.replace("EST", "").trim()}`);
    const nextDate = new Date(selectedTimeDate.getTime() + duration * 60 * 1000);

    const nextTimeString =
      nextDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      }) + " EST";

    // Try to find next consecutive appointment
    const nextConsecutiveAppointment = sameDayAppointments.find(
      (appt) => {
        const apptTime = appt.timeslot || appt.proposedTimeslot;
        return apptTime?.toUpperCase() === nextTimeString.toUpperCase();
      }
    );

    // If next consecutive appointment exists, return it
    if (nextConsecutiveAppointment) {
      return nextConsecutiveAppointment;
    }

    // If no next consecutive appointment, find the selected appointment index
    const selectedIndex = sameDayAppointments.findIndex(appt =>
      appt === selectedAppointment ||
      (appt.patientId === selectedAppointment.patientId &&
        (appt.timeslot || appt.proposedTimeslot) === selectedTime)
    );

    if (selectedIndex === -1) return null;

    // If selected appointment is the last one (by time), return second-to-last
    if (selectedIndex === sameDayAppointments.length - 1) {
      return sameDayAppointments.length >= 2 ? sameDayAppointments[sameDayAppointments.length - 2] : null;
    }

    // If there's no next consecutive appointment but selected isn't last,
    // just return the next appointment by time
    for (let i = selectedIndex + 1; i < sameDayAppointments.length; i++) {
      const currentTime = new Date(`1970-01-01 ${selectedTime.replace("EST", "").trim()}`);
      const potentialTime = sameDayAppointments[i].timeslot || sameDayAppointments[i].proposedTimeslot;
      const potentialTimeDate = new Date(`1970-01-01 ${potentialTime?.replace("EST", "").trim() || '00:00'}`);

      // Find the next appointment that comes after selected appointment
      if (potentialTimeDate > currentTime) {
        return sameDayAppointments[i];
      }
    }

    return null;
  }


  const handleSubmit = async (appointmentNote = "") => {
    setAddAppointmentError("");

    const nextAppt = getNextConsecutiveAppointment(providerAppointments, providerAppointment, createAppointment?.appointmentType?.duration);

    const type = appointmentTypes.find(
      (type) => type.value === Number(appointmentVisitType)
    );
    const visitDate = moment(providerAppointment?.date ?? selectedDate).format(
      "YYYY-MM-DD"
    );

    // Get InteractionGuideID from URL query parameters
    const interactionGuideId = new URLSearchParams(location.search).get('InteractionGuideID');

    // Prefer the market on the selected slot. When no provider is filtered,
    // that is the only way to keep a secondary-market result on the right market.
    const rawMarketId =
      createAppointment?.marketId ??
      providerAppointment?.marketId ??
      selectedProvider?.value?.provider_market_id ??
      null;
    const parsedMarketId = Number(rawMarketId);
    const marketId =
      rawMarketId == null || rawMarketId === "" || Number.isNaN(parsedMarketId)
        ? null
        : parsedMarketId;

    const payload = {
      activity: `Creating a new appointment for provider ${createAppointment.provider.name
        } and patient ${createAppointment.patient.name
        } on ${visitDate} with appointment type ${type?.label} by user ${user?.details?.name || ""
        }`,
      type: isPublished
        ? ACTIVITY_TYPE.DISPATCH_APPOINTMENT_PUBLISHED
        : ACTIVITY_TYPE.DISPATCH_APPOINTMENT_CREATED,
      data: {
        providerId: createAppointment.provider.id,
        patientId: createAppointment.patient.id,
        marketId,
      },
    };
    try {
      setIsPublished(false);
      const {data: appointment} = await createNewAppointment({
        appointment: {
          visitDate,
          sequenceNumber: createAppointment.sequenceNumber,
          appointmentType: appointmentVisitType,
          appointmentTypeId: type?.ehr_id,
          patientId: createAppointment.patient.id,
          providerId: createAppointment.provider.id,
          marketId,
          distanceFromPreviousAppointment: createAppointment.distance,
          durationFromPreviousAppointment: createAppointment.duration,
          distanceFromProvider: createAppointment.distanceFromHome,
          durationFromProvider: createAppointment.durationFromHome,
          timeslot: moment(createAppointment.timeslot, "h:mm A").format(
            "HH:mm"
          ),
          visitType,
          note: appointmentNote,
          visitStatus: createAppointment?.visitStatus,
          status: createAppointment?.status,
          matchScore: createAppointment?.matchScore,
          created_from: APPOINTMENT_CREATED_FROM.DISPATCH,
          visitLocationId:
            selectedVisitLocation === null
              ? null
              : selectedVisitLocation?.addressType === "PRIMARY"
                ? null
                : selectedVisitLocation?.id,
        },
        updates,
        isPublish: isPublished,
        practiceId: user?.organisation?.practiceId,
        departmentId: selectedPatient?.departmentId,
        interactionGuideId: interactionGuideId ?? null,

        lockedSlot: (searchConsecutiveSlots && nextAppt) ? {
          providerId: nextAppt.providerId,
          timeslot: moment(nextAppt.proposedTimeslot, "h:mm A").format(
            "HH:mm"
          ),
          date: nextAppt.date,
          locked_by_id: user?.id,
          locked_by_name: user?.details?.name,
          duration: createAppointment.appointmentType?.duration,
          location: selectedVisitLocation?.id || null,
        } || null : null
      }).unwrap();
      setCreateAppointment({
        ...createAppointment,
        visitDate: moment(providerAppointment?.date ?? selectedDate).format(
          "MM/DD/YYYY"
        ),
        appointmentType: {
          id: appointmentVisitType,
          name: type?.label,
          duration: type?.duration,
        },
      });
      await createLog({
        ...payload,
        data: {
          ...payload.data,
          id: appointment.id,
          timezone: appointment.timezone,
          targetId: appointment.targetId,
          timeslot: appointment.timeslot
        },
        activity: `${payload.activity} CareSMS Appt Id: ${appointment.id}, Target Id: ${appointment.targetId}, Timeslot: ${appointment.timeslot} ${appointment.timezone ?? ""}`,
        status: ACTIVITY_STATUS.SUCCESS,
      }).unwrap();
      setOpen(true);
      resetAllandFetch();
    } catch (error) {
      const apiMessage =
        (typeof error?.data?.message === "string" && error.data.message) ||
        (typeof error?.data?.error === "string" && error.data.error) ||
        "Unable to add the appointment. Please try again.";
      setAddAppointmentError(apiMessage);
      await createLog({
        ...payload,
        activity:
          payload.activity +
          " Reason: " +
          (error?.data?.message ?? error?.message),
        status: ACTIVITY_STATUS.ERROR,
      }).unwrap();
      console.log(error);
      // setAlert({
      //   message:
      //     "(Error Code 001): We were unable to publish the appointment to CareSMS due to system timeout. Please try again in a few minutes. If the problem persists, please contact your system administrator.",
      //   severity: "error",
      // });
    }
  };

  useEffect(() => {
    if (!openConfirmModal) {
      setAddAppointmentError("");
    }
  }, [openConfirmModal]);

  useEffect(() => {
    if (updatedAppointments.length === 1 && openConfirmModal) {
      setUpdatedAppointments(updatedAppointments.map((appointment) => ({
        ...appointment,
        timeslot: providerAppointment?.proposedTimeslot
      })));

      setCreateAppointment((prev) => ({ ...prev, timeslot: providerAppointment?.proposedTimeslot }));
    }
  }, [openConfirmModal]);

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] gap-4 pt-8 overflow-scroll">
      <Snackbar
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={openAlert}
        autoHideDuration={5000}
        message={message ? message : "Something went wrong"}
        severity="error"
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity ? severity : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message ? message : "Something went wrong"}
        </Alert>
      </Snackbar>
      <Loading loading={isLoading} />
      <div className="flex justify-between items-center">
        <h1 className="px-8 m-0 text-2xl font-bold">Dispatch</h1>
        {user?.permissions?.includes('dispatch.hide_filters') && <Tooltip title={filterOpen ? "Hide filters" : "Show filters"}>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="px-2 mr-6"
          >
            <Icon name="20-filter" />
          </button>
        </Tooltip>}
      </div>
      {filterOpen && (
        <FilterComponentV2
          patients={patients}
          selectedPatient={selectedPatient}
          setLanguagePreference={setLanguagePreference}
          setGenderPreference={setGenderPreference}
          setSelectedPatient={setSelectedPatient}
          setSelectedProvider={setSelectedProvider}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setAppointmentType={setAppointmentType}
          setAppointmentVisitType={setAppointmentVisitType}
          providers={providers}
          appointmentTypes={appointmentTypes}
          selectedProvider={selectedProvider}
          startDate={startDate}
          endDate={endDate}
          appointmentType={appointmentType}
          onSubmitFilters={onSubmitFilters}
          patientPromiseOptions={patientOptions}
          providerPromiseOptions={providerOptions}
          visitTypePromiseOptions={visitTypeOptions}
          visitTypeFetchImmediate={visitTypePromiseOptions}
          prospectType={prospectType}
          setProspectType={setProspectType}
          routingCriteria={routingCriteria}
          setRoutingCriteria={setRoutingCriteria}
          languagePreference={languagePreference}
          genderPreference={genderPreference}
          onResetFilters={resetAllandFetch}
          providerType={providerType}
          setProviderType={setProviderType}
          timeOfDay={timeOfDay}
          setTimeOfDay={setTimeOfDay}
          selectedVisitLocation={selectedVisitLocation}
          setSelectedVisitLocation={setSelectedVisitLocation}
          prospectTypeOptions={PROSPECT_TYPE_OPTIONS}
          providerTypeOptions={PROVIDER_TYPE_OPTIONS}
          timeOfDayOptions={TIME_OF_DAY_OPTIONS}
          searchConsecutiveSlots={searchConsecutiveSlots}
          setSearchConsecutiveSlots={setSearchConsecutiveSlots}
          showAlert={showAlert}
        />
      )}
      <div className="w-full h-[1px] bg-gray-200" />
      {dates?.length > 0 ? (
        selectedPatient !== null && (
          <div className="flex w-full">
            <div className="flex flex-col gap-4 px-8 w-full">
              <DateFilter
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                dates={dates}
                options={dispatchSortOptions}
                setSelectedOption={setSelectedOption}
                setView={setView}
                view={view}
                startDate={startDate}
                endDate={endDate}
                possibleInsertionsPerGroup={possibleInsertionsPerGroup}
              />

              <div
                ref={containerRef}
                className={classNames(
                  "flex overflow-y-auto flex-col gap-4",
                  filterOpen ? "h-[calc(100vh-520px)]" : "h-[calc(100vh-260px)]"
                )}
                onScroll={handleScroll}
              >
                <TableComponent
                  providerAppointments={providerAppointments}
                  setOpenProviderAppointments={setOpenProviderAppointments}
                  fetchAppointmentsFunction={fetchAppointmentsFunction}
                  setProviderAppointment={setProviderAppointment}
                  providerAppointment={providerAppointment}
                  view={view}
                  providersData={providersData}
                  setOpenMap={setOpenMap}
                  setOpenConfirmModal={setOpenConfirmModal}
                  setIsPublished={setIsPublished}
                  appointmentType={appointmentType}
                  selectedPatient={selectedPatient}
                  user={user}
                  routingCriteria={routingCriteria}
                  searchConsecutiveSlots={searchConsecutiveSlots}
                  dispatchTiming={dispatchTimingForResultsRef.current}
                  onDispatchEtaStarted={markDispatchEtaStarted}
                  onDispatchEtaCompleted={markDispatchEtaCompleted}
                  onDispatchRenderReady={markDispatchRenderReady}
                />
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-4 items-center justify-center h-[calc(100vh-320px)]">
          {possibleInsertionsPerGroup?.under50 ||
            possibleInsertionsPerGroup?.under75 ||
            possibleInsertionsPerGroup?.under100 ||
            possibleInsertionsPerGroup?.above100 ? (
            <div className="flex flex-col gap-0 items-start">
              <p className="self-start m-0 text-lg font-bold">
                Providers available in the following ranges (
                {moment(startDate).format("MMMM DD")} -{" "}
                {moment(endDate).format("MMMM DD")})
              </p>
              <div className="flex gap-2 items-center">
                <p className="text-sm font-normal">
                  <span className="font-bold">{`< 50 miles: `}</span>
                  {possibleInsertionsPerGroup?.under50 || "0"}
                  {" | "}
                </p>
                <p className="text-sm font-normal">
                  <span className="font-bold">{`< 75 miles: `}</span>
                  {possibleInsertionsPerGroup?.under75 || "0"}
                  {" | "}
                </p>
                <p className="text-sm font-normal">
                  <span className="font-bold">{`< 100 miles: `}</span>
                  {possibleInsertionsPerGroup?.under100 || "0"}
                  {" | "}
                </p>
                <p className="text-sm font-normal">
                  <span className="font-bold">{`≥ 100 miles: `}</span>
                  {possibleInsertionsPerGroup?.above100 || "0"}
                </p>
              </div>
            </div>
          ) : dispatchErrors && dispatchErrors.length > 0 ? (
            <div className="flex flex-col gap-2 text-left">
              <p className="m-0 text-lg font-bold">
                <span className="m-0 font-bold">
                  No providers available for this search
                </span>
              </p>
              {dispatchErrors.map((error, index) => (
                <p key={index} className="m-0">
                  {index + 1}. {error}
                </p>
              ))}
            </div>
          ) : (
            <p>
              Select a patient first to automatically optimize visit scheduling
            </p>
          )}
        </div>
      )}
      {openProviderAppointments && (
        <PatientSchedulingModal
          providerAppointment={providerAppointment}
          departmentId={
            patients?.find(
              (patient) => patient.patientId === selectedPatient?.value
            )?.patientDepartmentId
          }
          selectedDate={selectedDate}
          openProviderAppointments={openProviderAppointments}
          setOpenProviderAppointments={setOpenProviderAppointments}
          setCreateAppointment={setCreateAppointment}
          updatedAppointments={updatedAppointments}
          setUpdatedAppointments={setUpdatedAppointments}
          setOpenMap={setOpenMap}
          setIsPublished={setIsPublished}
          createLoad={createLoad}
          setOpenConfirmModal={setOpenConfirmModal}
          view={view}
          handleSubmit={() => {
            setAppointmentVisitType(appointmentType?.value);
          }}
        />
      )}
      {openConfirmModal && (
        <AddAppointment
          openConfirmModal={openConfirmModal}
          setOpenConfirmModal={setOpenConfirmModal}
          handleSubmit={handleSubmit}
          visitType={visitType}
          setVisitType={setVisitType}
          appointmentType={appointmentType}
          appointmentTypes={appointmentTypes}
          appointmentVisitType={appointmentVisitType}
          setAppointmentVisitType={setAppointmentVisitType}
          isLoading={createLoad}
          errorMessage={addAppointmentError}
        />
      )}
      {open && (
        <ScheduleConfirmation
          open={open}
          handleClose={handleClose}
          details={createAppointment}
        />
      )}
      {openMap && (
        <Map
          open={openMap}
          setOpen={setOpenMap}
          uniqueLocations={uniqueLocations}
          provider={{
            ...providerAppointment,
            ...appointments.provider,
          }}
          providers={[]}
          selectedPatient={appointments.patient}
        />
      )}
      {!!filterError && (
        <InfoModal
          open={!!filterError}
          handleClose={() => setFilterError(null)}
          title="Required fields"
          subtext={filterError}
        />
      )}
    </div>
  );
};

export default PatientSchedulingWizard;
